import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function SSOLogin() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('Full URL:', window.location.href);
    console.log('Location search:', location.search);

    const handleSSOLogin = async (token) => {
      try {
        console.log('Starting SSO login process...');

        // DON'T decode with atob() - the token is a JWT, not base64-encoded JSON
        // Send the JWT directly to your backend for verification
        console.log('Sending token to backend for verification');

        const response = await fetch('https://payvoucher.netgateerp.lk/api/users/verify-sso', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token }) // Send the JWT token directly
        });

        if (!response.ok) {
          throw new Error('Token verification failed');
        }

        const data = await response.json();

        if (!data.user || !data.user.email) {
          throw new Error('Invalid user data received');
        }

        console.log('Processing SSO login for:', data.user.email);

        const userWithRole = {
          ...data.user,
          role: data.user.role || 'admin' // Use role from backend or default
        };

        // Store the authenticated session
        localStorage.setItem('token', data.localAccessToken);
        localStorage.setItem('user', JSON.stringify(userWithRole));

        console.log('SSO verification successful');

        // Small delay to ensure state updates propagate
        await new Promise(resolve => setTimeout(resolve, 100));

        console.log('Navigating to dashboard...');

        // Check if there is a redirect path with invoice data (from To-Be-Payment app)
        const redirectPath = searchParams.get('redirect');
        if (redirectPath) {
          // Preserve invoice data params for the payment voucher form
          const invoiceParams = new URLSearchParams();
          const payableTo = searchParams.get('payableTo');
          const grnNo = searchParams.get('grnNo');
          const amount = searchParams.get('amount');
          const description = searchParams.get('description');
          const invoiceDate = searchParams.get('invoiceDate');
          // Multi-invoice prefill: JSON-encoded `items` and comma-separated `invoiceIds`
          const items = searchParams.get('items');
          const invoiceIds = searchParams.get('invoiceIds');

          if (payableTo) invoiceParams.set('payableTo', payableTo);
          if (grnNo) invoiceParams.set('grnNo', grnNo);
          if (amount) invoiceParams.set('amount', amount);
          if (description) invoiceParams.set('description', description);
          if (invoiceDate) invoiceParams.set('invoiceDate', invoiceDate);
          if (items) invoiceParams.set('items', items);
          if (invoiceIds) invoiceParams.set('invoiceIds', invoiceIds);

          const queryString = invoiceParams.toString();
          const targetUrl = queryString ? `${redirectPath}?${queryString}` : redirectPath;
          console.log('Redirecting to:', targetUrl);
          navigate(targetUrl, { replace: true });
        } else {
          // Navigate to dashboard
          navigate('/home', { replace: true });
        }

      } catch (err) {
        console.error('SSO Login Error:', err);
        setError(err.message || 'Authentication failed. Please try again.');
        setLoading(false);
      }
    };

    // Get token from URL parameters
    const searchParams = new URLSearchParams(location.search);
    console.log('Search params string:', searchParams.toString());

    let ssoToken = searchParams.get('token');

    if (ssoToken) {
      // Decode URL encoding if present
      try {
        ssoToken = decodeURIComponent(ssoToken);
        console.log('SSO Token found and decoded');
        handleSSOLogin(ssoToken);
      } catch (decodeError) {
        console.error('Token decode error:', decodeError);
        setError('Invalid token format');
        setLoading(false);
      }
    } else {
      console.log('No SSO token in URL');
      setError('No SSO token provided');
      setLoading(false);
    }
  }, [location.search, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full opacity-20 animate-ping"></div>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-3">
            Authenticating...
          </h2>
          <p className="text-slate-600 text-lg">
            Please wait while we verify your credentials
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Authentication Failed
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">{error}</p>
            <p className="text-sm text-gray-500 mb-4">
              Failed to execute 'atob' on 'Window': The string to be decoded is not correctly encoded.
            </p>
            <p className="text-xs text-gray-400 mb-4 break-all">URL: {window.location.href}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Go to Home
              </button>
              <button
                onClick={() => window.close()}
                className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}